// ============================================================
//  src/admin/pages/AdminRentSpacePage.js
//
//  Configure the platform's own WhatsApp token/WABA/monthly price
//  for "Rent Space", and manage every rental: pause, resume, or
//  permanently delete (calls Meta's DELETE /{PHONE_NUMBER_ID} --
//  irreversible, guarded by a strict confirmation).
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getRentSpaceConfig, saveRentSpaceConfig,
  listRentSpaceRentals, pauseRental, resumeRental, deregisterRental, verifyRentalStatus,
  previewLeakedFlows, cleanupLeakedFlows, syncPlatformIdsToBots, listBotFlows, removeBotFlows,
  getRentSpaceUsage,
} from '../api/adminClient';
import { PageHeader } from '../components/UI';

const EMPTY_CONFIG = { token: '', wabaId: '', appSecret: '', monthlyPrice: '', flowEndpointUrl: '', metaAppId: '', maxPerUser: '' };

const STATUS_META = {
  pending_payment: { label: 'Pending Payment', color: '#9a7b00', bg: '#fff8e1' },
  active:          { label: 'Active',          color: '#00875a', bg: '#e7f9f0' },
  paused:          { label: 'Paused',           color: '#6b7280', bg: '#f3f4f6' },
  deregistered:    { label: 'Deregistered',     color: '#c0392b', bg: '#fdeaea' },
};

// NOTE: Meta's API does not support deleting a phone number — confirmed
// directly against their current docs ("You can't delete a business
// phone number via the API. To delete a business phone number, use
// WhatsApp Manager."). What IS available is deregistration, which stops
// the number sending/receiving through Cloud API but does NOT remove it
// from the WABA's number list, free up a slot in the 20-number cap, or
// delete its message history. This modal is worded to reflect that
// honestly rather than promising a "delete" this platform can't do.
function DeregisterConfirmModal({ rental, onClose, onDeregistered }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy]   = useState(false);
  const expectedPhone = `${rental.rnCountryCode || ''}${rental.rnPhoneNumber || ''}`;

  async function handleDeregister() {
    setBusy(true);
    try {
      const res = await deregisterRental(rental.rnId, typed.trim());
      if (res.data?.success) {
        toast.success(res.data.message ?? 'Number deregistered');
        onDeregistered();
        onClose();
      } else {
        toast.error(res.data?.message ?? 'Could not deregister this number');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setBusy(false);
  }

  return (
    <div className="bf-modal-overlay" onClick={onClose}>
      <div className="bf-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="bf-modal-icon bf-modal-icon-danger">⚠️</div>
        <div className="bf-modal-title">Deregister Phone Number</div>
        <div className="bf-modal-message">
          This stops {expectedPhone} from sending or receiving messages through Cloud API immediately — any bot
          using this Phone Number ID stops working the instant this completes.
          <br /><br />
          <strong>This is not the same as deleting the number.</strong> Meta's API doesn't support deletion — it
          stays on the WABA's number list and still counts toward the 20-number cap. To fully delete it (and free up
          that slot), a business portfolio admin has to do that manually inside WhatsApp Manager, and only if the
          number hasn't sent a paid message in the last 30 days.
        </div>
        <div className="bf-field" style={{ marginTop: 16 }}>
          <label className="bf-field-label">
            Type the exact phone number (<code>{expectedPhone}</code>) to confirm
          </label>
          <input className="bf-input" value={typed} onChange={e => setTyped(e.target.value)} placeholder={expectedPhone} />
        </div>
        <div className="bf-modal-actions">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-danger" onClick={handleDeregister} disabled={busy || typed.trim() !== expectedPhone}>
            {busy ? 'Deregistering…' : 'Deregister Number'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Manual per-bot flow review/cleanup. Unlike LeakedFlowsPanel's automatic
// duplicate detection, this works even when a leaked flow only ever got
// synced onto ONE rental bot -- the common case, since it just takes one
// customer clicking "Sync" once to pull in everything on the shared WABA.
// The admin (working with the customer, who knows which flows are
// actually theirs) checks off the ones that aren't and removes them --
// local rows only, nothing on Meta is touched.
function BotFlowsReviewModal({ bId, onClose }) {
  const [flows, setFlows]     = useState(null); // null = loading
  const [checked, setChecked] = useState({});   // fId -> bool
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    setFlows(null);
    listBotFlows(bId).then(res => {
      if (res.data?.success) setFlows(res.data.flows || []);
      else { toast.error(res.data?.message ?? 'Could not load flows'); setFlows([]); }
    }).catch(() => { toast.error('Network error'); setFlows([]); });
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  function toggle(fId) { setChecked(c => ({ ...c, [fId]: !c[fId] })); }
  const selectedIds = Object.keys(checked).filter(k => checked[k]); // fId is a string ID, not numeric -- keep as-is

  async function handleRemove() {
    if (!selectedIds.length) return;
    setRemoving(true);
    try {
      const res = await removeBotFlows(bId, selectedIds);
      if (res.data?.success) {
        toast.success(res.data.message ?? 'Removed');
        setChecked({});
        load();
      } else {
        toast.error(res.data?.message ?? 'Could not remove');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setRemoving(false);
  }

  return (
    <div className="bf-modal-overlay" onClick={onClose}>
      <div className="bf-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="bf-modal-title">Review Flows on This Bot</div>
        <div className="bf-modal-message">
          Check off any flow this customer didn't actually create — e.g. ones that leaked in from another renter
          before syncing was blocked for rented numbers — then remove them. This only removes the local row; nothing
          on Meta is touched, and any other renter's own copy is unaffected.
        </div>

        {flows === null ? (
          <div style={{ padding: '16px 0', fontSize: 13, color: '#9ca3af' }}>Loading…</div>
        ) : flows.length === 0 ? (
          <div style={{ padding: '16px 0', fontSize: 13, color: '#9ca3af' }}>This bot has no flows.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', margin: '14px 0' }}>
            {flows.map(f => (
              <label key={f.fId} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer',
              }}>
                <input type="checkbox" checked={!!checked[f.fId]} onChange={() => toggle(f.fId)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{f.fName}</div>
                  <div style={{ fontSize: 11.5, color: '#9ca3af' }}>
                    {f.fCategory} · {f.fStatus} · {f.fCreatedAt ? new Date(f.fCreatedAt).toLocaleDateString() : ''}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="bf-modal-actions">
          <button className="btn btn-outline" onClick={onClose} disabled={removing}>Close</button>
          <button className="btn btn-danger" onClick={handleRemove} disabled={removing || !selectedIds.length}>
            {removing ? 'Removing…' : `Remove ${selectedIds.length || ''} Selected`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}

// Pushes the current platform token/WABA ID/App Secret/Meta App ID onto
// every existing rental bot in one pass -- covers rental bots created
// before Bot::createBot() was fixed to persist those fields at all (see
// that method's comment), and bots created before Meta App ID existed as
// a platform setting.
function SyncPlatformIdsPanel() {
  const [syncing, setSyncing] = useState(false);

  async function run() {
    setSyncing(true);
    try {
      const res = await syncPlatformIdsToBots();
      if (res.data?.success) toast.success(res.data.message ?? 'Synced');
      else toast.error(res.data?.message ?? 'Sync failed');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setSyncing(false);
  }

  return (
    <div className="bf-card">
      <div className="bf-card-hdr">Sync to Existing Rented Bots</div>
      <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Pushes the token, WABA ID, App Secret, and Meta App ID above onto every bot already linked to a rental —
          and repairs a blank Phone Number ID on any of them from that rental's own record (needed for encryption
          key setup, among other things).
          Useful right after changing any of those, or to fix an older rented bot that's missing one of them.
        </div>
        <button className="btn btn-outline" style={{ alignSelf: 'flex-start', fontSize: 12, padding: '6px 12px' }}
          onClick={run} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </div>
    </div>
  );
}

// One-time repair panel for the Flow-sync leak: before "Sync from Meta"
// was blocked for rented numbers, any renter who clicked it pulled in
// every Flow on the shared platform WABA, including ones OTHER renters
// had created, as local rows on their own bot. This previews (and can
// remove) exactly those cross-contaminated rows -- see
// RentSpaceController::adminCleanupLeakedFlows for the detection logic.
// Nothing on Meta's side is touched, only the local duplicate rows.
function LeakedFlowsPanel() {
  const [preview, setPreview] = useState(null); // { groups, removeCount } | null
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  async function runPreview() {
    setLoading(true);
    try {
      const res = await previewLeakedFlows();
      if (res.data?.success) setPreview(res.data);
      else toast.error(res.data?.message ?? 'Could not check for leaked flows');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setLoading(false);
  }

  async function runCleanup() {
    setCleaning(true);
    try {
      const res = await cleanupLeakedFlows();
      if (res.data?.success) {
        toast.success(res.data.message ?? 'Cleaned up');
        setPreview(null);
      } else {
        toast.error(res.data?.message ?? 'Cleanup failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setCleaning(false);
  }

  return (
    <div className="bf-card">
      <div className="bf-card-hdr">Leaked Flows Check (Rented Numbers)</div>
      <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Before "Sync from Meta" was blocked for rented numbers, a renter who used it would pull in every Flow on
          the shared platform WABA — including ones other renters created — as local rows on their own bot. This
          checks for exactly that (and only that) and can clean it up. Nothing on Meta is touched, only local
          duplicate rows.
        </div>
        <button className="btn btn-outline" style={{ alignSelf: 'flex-start', fontSize: 12, padding: '6px 12px' }}
          onClick={runPreview} disabled={loading}>
          {loading ? 'Checking…' : 'Check for leaked flows'}
        </button>

        {preview && preview.removeCount === 0 && (
          <div style={{ fontSize: 13, color: 'var(--bf-success)' }}>✅ No cross-contaminated flows found.</div>
        )}

        {preview && preview.removeCount > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--bf-warning)' }}>
              Found {preview.removeCount} leaked flow row{preview.removeCount !== 1 ? 's' : ''} across {preview.groups.length} flow{preview.groups.length !== 1 ? 's' : ''}.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
              {preview.groups.map(g => (
                <div key={g.metaFlowId} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 12.5 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>{g.keep.fName}</strong>{' '}
                    <span style={{ color: '#9ca3af' }}>— kept for {g.keep.ownerEmail || g.keep.ownerName}</span>
                  </div>
                  {g.remove.map(r => (
                    <div key={r.fId} style={{ color: '#c0392b' }}>
                      🗑 remove copy from {r.ownerEmail || r.ownerName} ("{r.fName}")
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <button className="btn btn-danger" style={{ alignSelf: 'flex-start', fontSize: 12, padding: '6px 14px' }}
              onClick={runCleanup} disabled={cleaning}>
              {cleaning ? 'Removing…' : `Remove ${preview.removeCount} leaked row(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Template & message costs per rented number ────────────────
// Every rented number is on the platform's own WABA, so Meta bills the
// platform for every template a renter sends. This splits Meta's bill by
// phone number (pricing_analytics) so you can see what each renter cost.
const RSU_RANGES = [
  { v: 'today', label: 'Today' },
  { v: '7d', label: '7 days' },
  { v: '30d', label: '30 days' },
  { v: 'this_month', label: 'This month' },
  { v: 'last_month', label: 'Last month' },
  { v: 'custom', label: 'Custom' },
];
const RSU_CATS = [
  { k: 'MARKETING', label: 'Marketing', color: '#7C3AED' },
  { k: 'UTILITY', label: 'Utility', color: '#2563EB' },
  { k: 'AUTHENTICATION', label: 'Authentication', color: '#D97706' },
  { k: 'SERVICE', label: 'Service', color: '#059669' },
];
const rsuCatLabel = k => RSU_CATS.find(c => c.k === k)?.label ?? k.charAt(0) + k.slice(1).toLowerCase().replace(/_/g, ' ');

function rsuMoney(n, currency) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: v !== 0 && Math.abs(v) < 1 ? 4 : 2 }).format(v);
  } catch { return `${currency || ''} ${v.toFixed(2)}`.trim(); }
}

function rsuCsv(data) {
  const cats = RSU_CATS.map(c => c.k);
  const head = ['Phone', 'Name', 'Owner', 'Email', 'Status', ...cats.map(c => `${rsuCatLabel(c)} cost`), 'Total cost', 'Currency', 'Messages', 'Billable messages'];
  const row = (r, isOther) => [
    r.phone, r.displayName ?? r.name ?? '', isOther ? '(not a rental)' : r.owner, r.email ?? '', r.status ?? '',
    ...cats.map(c => r.byCategory?.[c]?.cost ?? 0), r.cost, data.currency, r.volume, r.billable,
  ];
  const lines = [head, ...data.rentals.map(r => row(r, false)), ...data.other.map(r => row(r, true))]
    .map(cols => cols.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `rent-space-costs-${(data.range?.label || 'range').replace(/[^\w-]+/g, '-').toLowerCase()}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function rsuOurs(r) {
  const o = r.ours;
  if (!o) return null;
  const b = o.broadcast ?? {}; const u = o.updates ?? {};
  return {
    attempts: (b.total || 0) + (u.sent || 0) + (u.failed || 0),
    failed: (b.failed || 0) + (u.failed || 0),
    billing: (b.billing || 0) + (u.billing || 0),
    b, u, flag: o.paymentFlag, detail: o.paymentDetail,
  };
}

function RentUsagePanel() {
  const [range, setRange] = useState('30d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);       // rnId / phone expanded
  const [showIdle, setShowIdle] = useState(false);
  const [wabaId, setWabaId] = useState('');      // '' = the Rent Space account

  const load = useCallback(async (refresh = false) => {
    if (range === 'custom' && (!from || !to)) return;
    setLoading(true); setError('');
    try {
      const res = await getRentSpaceUsage({ range, from, to, refresh, wabaId: wabaId || undefined });
      if (res.data?.success) setData(res.data);
      else setError(res.data?.message ?? 'Could not load costs');
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Network error');
    }
    setLoading(false);
  }, [range, from, to, wabaId]);

  useEffect(() => { load(false); }, [load]);

  const cur = data?.currency;
  const rentals = data?.rentals ?? [];
  const active = rentals.filter(r => r.volume > 0 || r.cost > 0);
  const idle = rentals.filter(r => !(r.volume > 0 || r.cost > 0));
  const shown = showIdle ? rentals : active;
  const rentTotal = active.reduce((a, r) => a + r.cost, 0);
  const maxCost = Math.max(0.0001, ...rentals.map(r => r.cost), ...(data?.other ?? []).map(r => r.cost));

  function Row({ r, isOther }) {
    const id = isOther ? `o:${r.phone}` : r.rnId;
    const isOpen = open === id;
    return (
      <>
        <button type="button" className={`rsu-row ${isOpen ? 'is-open' : ''}`} onClick={() => setOpen(isOpen ? null : id)}>
          <div className="rsu-who">
            <b>{r.phone || 'Unknown number'}</b>
            <span>{isOther ? (r.name || 'Not a rental') : <>{r.owner || r.email}{r.displayName ? ` · ${r.displayName}` : ''}</>}</span>
            {!isOther && r.onWaba === false && (
              <em className="rsu-flag">⚠ This number isn’t on the account shown here — its charges are billed to another WhatsApp Business account</em>
            )}
            {!isOther && r.botWabaId && data?.wabaId && r.botWabaId !== data.wabaId && (
              <em className="rsu-flag" style={{ color: '#92400e' }}>Bot is set to account {r.botWabaId}</em>
            )}
            {!isOther && rsuOurs(r)?.billing > 0 && (
              <em className="rsu-flag">⛔ {rsuOurs(r).billing.toLocaleString()} rejected by Meta billing — not charged</em>
            )}
          </div>
          <div className="rsu-bar" aria-hidden="true">
            {RSU_CATS.map(c => {
              const v = r.byCategory?.[c.k]?.cost ?? 0;
              return v > 0 ? <span key={c.k} style={{ width: `${(v / maxCost) * 100}%`, background: c.color }} /> : null;
            })}
          </div>
          <div className="rsu-num">
            <b>{rsuMoney(r.cost, cur)}</b>
            <span>{r.billable.toLocaleString()} paid · {r.volume.toLocaleString()} total</span>
          </div>
          <span className="rsu-chev">{isOpen ? '▴' : '▾'}</span>
        </button>
        {isOpen && (
          <div className="rsu-detail">
            {!isOther && (() => {
              const o = rsuOurs(r);
              if (!o) return <div className="rsu-ours"><b>What CommandKit saw</b><span className="rsu-muted">No broadcasts or delivery updates recorded for this rental’s bot in this range.</span></div>;
              return (
                <div className="rsu-ours">
                  <b>What CommandKit saw</b>
                  <div className="rsu-ours-grid">
                    <div><span>Broadcast messages</span><strong>{(o.b.total || 0).toLocaleString()}</strong><em>{(o.b.delivered || 0).toLocaleString()} delivered · {(o.b.failed || 0).toLocaleString()} failed</em></div>
                    <div><span>Other sends (delivery updates)</span><strong>{((o.u.sent || 0) + (o.u.failed || 0)).toLocaleString()}</strong><em>{(o.u.delivered || 0).toLocaleString()} delivered · {(o.u.failed || 0).toLocaleString()} failed</em></div>
                    <div className={o.billing > 0 ? 'is-bad' : ''}><span>Rejected by Meta billing</span><strong>{o.billing.toLocaleString()}</strong><em>error 131042 / 131045 · never charged</em></div>
                  </div>
                  {Object.keys(o.u.paid || {}).length > 0 && (
                    <span className="rsu-muted">Meta marked as paid when sent: {Object.entries(o.u.paid).map(([k, n]) => `${n.toLocaleString()} ${k}`).join(' · ')}</span>
                  )}
                  {(() => {
                    const ourPaid = Object.values(o.u.paid || {}).reduce((a, n) => a + n, 0);
                    if (ourPaid > 0 && r.billable === 0) return <span className="rsu-flag" style={{ color: '#92400e' }}>⚠ Meta priced {ourPaid.toLocaleString()} message{ourPaid === 1 ? '' : 's'} from this number as paid when sending, but its cost report doesn’t show them yet — Meta’s report can lag. Refresh later.</span>;
                    return null;
                  })()}
                  {o.b.topError && <span className="rsu-muted">Most common broadcast error: “{o.b.topError}”</span>}
                  {o.flag && <span className="rsu-flag">⛔ This bot is currently flagged by Meta billing{o.detail ? `: ${o.detail}` : ''}</span>}
                </div>
              );
            })()}
            <div className="rsu-cats">
              {Object.entries(r.byCategory || {}).length === 0 ? <span className="rsu-muted">No messages in this range.</span>
                : Object.entries(r.byCategory).sort((a, b) => b[1].cost - a[1].cost).map(([k, v]) => (
                  <div key={k} className="rsu-cat">
                    <i style={{ background: RSU_CATS.find(c => c.k === k)?.color ?? '#9ca3af' }} />
                    <span>{rsuCatLabel(k)}</span>
                    <b>{rsuMoney(v.cost, cur)}</b>
                    <em>{v.volume.toLocaleString()} msgs</em>
                  </div>
                ))}
            </div>
            {r.daily?.length > 0 && (
              <div className="rsu-days">
                <div className="rsu-days-head"><span>Day</span><span>Messages</span><span>Cost</span></div>
                {[...r.daily].reverse().map(d => (
                  <div key={d.date} className="rsu-day">
                    <span>{new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    <span>{d.volume.toLocaleString()}</span>
                    <b>{rsuMoney(d.cost, cur)}</b>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="bf-card rsu">
      <div className="bf-card-hdr rsu-hdr">
        <span>💳 Template &amp; message costs per rented number</span>
        <div className="rsu-hdr-actions">
          {data && <button type="button" className="btn btn-outline rsu-sm" onClick={() => rsuCsv(data)}>⬇ CSV</button>}
          <button type="button" className="btn btn-outline rsu-sm" onClick={() => load(true)} disabled={loading}>{loading ? '…' : '↻ Refresh'}</button>
        </div>
      </div>
      <div className="bf-card-body rsu-body">
        <div className="rsu-ranges" role="tablist">
          {RSU_RANGES.map(r => (
            <button key={r.v} type="button" role="tab" aria-selected={range === r.v} className={range === r.v ? 'is-on' : ''} onClick={() => setRange(r.v)}>{r.label}</button>
          ))}
        </div>
        {range === 'custom' && (
          <div className="rsu-custom">
            <input type="date" className="bf-input" value={from} onChange={e => setFrom(e.target.value)} />
            <span>to</span>
            <input type="date" className="bf-input" value={to} onChange={e => setTo(e.target.value)} />
          </div>
        )}

        {wabaId && (
          <div className="rsu-viewing">
            <span>Showing WhatsApp Business account <b>{wabaId}</b></span>
            <button type="button" className="rsu-link" onClick={() => setWabaId('')}>← Back to the Rent Space account</button>
          </div>
        )}

        {error ? (
          <div className="rsu-error">⚠️ {error}</div>
        ) : !data ? (
          <div className="rsu-muted">{loading ? 'Asking Meta…' : range === 'custom' ? 'Pick a start and end date.' : ''}</div>
        ) : (
          <>
            <div className="rsu-stats">
              <div><span>Meta charged ({data.range.label})</span><b>{rsuMoney(data.total.cost, cur)}</b><em>whole WABA</em></div>
              <div><span>From rented numbers</span><b>{rsuMoney(rentTotal, cur)}</b><em>{active.length} of {rentals.length} rentals sent messages</em></div>
              <div><span>Paid messages</span><b>{data.total.billable.toLocaleString()}</b><em>{data.total.volume.toLocaleString()} incl. free</em></div>
            </div>
            <div className="rsu-legend">
              {Object.entries(data.byCategory || {}).sort((a, b) => b[1].cost - a[1].cost || b[1].volume - a[1].volume).map(([k, v]) => (
                <span key={k}><i style={{ background: RSU_CATS.find(c => c.k === k)?.color ?? '#9ca3af' }} />{rsuCatLabel(k)} {rsuMoney(v.cost, cur)} · {v.volume.toLocaleString()} msgs</span>
              ))}
            </div>

            {(data.blockedWabas || []).filter(b => b.wabaId !== data.wabaId).map(b => (
              <div key={b.wabaId} className="rsu-error is-red">
                <b>Meta says account {b.wabaId} has unpaid bills</b> — that’s where {b.rentals.join(', ')}’s blocked messages are billed,
                and it isn’t the account shown here ({data.wabaId}). Its charges won’t appear in this report.
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="btn btn-outline rsu-sm" onClick={() => setWabaId(b.wabaId)}>Show costs for account {b.wabaId}</button>
                </div>
              </div>
            ))}
            {(data.blockedWabas || []).some(b => b.wabaId === data.wabaId) && (
              <div className="rsu-error is-red">
                <b>Meta says this account ({data.wabaId}) has unpaid bills</b> — paid templates from every number on it will fail with error 131042 until it’s settled.
              </div>
            )}

            {(() => {
              const blocked = rentals.reduce((a, r) => a + (rsuOurs(r)?.billing || 0), 0);
              return blocked > 0 ? (
                <div className="rsu-error is-red">
                  <b>{blocked.toLocaleString()} message{blocked === 1 ? '' : 's'} from renters were rejected by Meta</b> because the WhatsApp Business account they send through has an unpaid balance
                  (error 131042). Meta doesn’t charge for rejected messages, so they don’t add to any renter’s cost — settle that account’s balance in Meta’s billing hub to unblock them.
                </div>
              ) : null;
            })()}

            {data.total.volume > 0 && data.total.billable === 0 && (
              <div className="rsu-error">
                <b>No paid messages were delivered in this range</b> — only free messages (service replies within each number’s free monthly tier, or ad entry-point messages).
                If renters are sending templates and still see nothing here, Meta is rejecting them (for example error 131042,
                unpaid balance on this WABA). Rejected messages aren’t charged, so they don’t appear. Check <b>Last month</b> or a
                custom range to find where earlier charges came from.
              </div>
            )}

            {data.incomplete && (
              <div className="rsu-error is-red">Meta’s report was too long to read in full for this range — pick a shorter range for exact numbers.</div>
            )}

            {!data.perNumber && (
              <div className="rsu-error">Meta returned totals without a phone number on each line, so costs can’t be split per rental for this range. The account total above is still correct.</div>
            )}

            <div className="rsu-list">
              {shown.length === 0 ? <div className="rsu-muted">No rented number sent anything in this range.</div>
                : shown.map(r => <Row key={r.rnId} r={r} />)}
            </div>
            {idle.length > 0 && (
              <button type="button" className="rsu-link" onClick={() => setShowIdle(s => !s)}>
                {showIdle ? 'Hide rentals with no messages' : `Show ${idle.length} rental${idle.length === 1 ? '' : 's'} with no messages`}
              </button>
            )}

            {data.other?.length > 0 && (
              <>
                <div className="rsu-sub">Other numbers on this WABA (not rentals)</div>
                <div className="rsu-list">{data.other.map(r => <Row key={`o:${r.phone}`} r={r} isOther />)}</div>
              </>
            )}

            <div className="rsu-note">
              From Meta’s pricing analytics for <b>{data.account || 'the platform WABA'}</b>, in the account’s currency ({cur || '—'}).
              Meta calls these figures approximate and they can lag by a few hours. “Paid” is what Meta charged for: templates, and since 1 Oct 2026 also service messages past each number’s 1,000 free a month and utility messages inside the 24-hour window. Free entry-point (ad) messages stay free.
              “What CommandKit saw” uses broadcast results plus delivery updates recorded from this update onwards.
              {data.points != null ? ` Read ${Number(data.points).toLocaleString()} entries across ${data.pages} page${data.pages === 1 ? '' : 's'} of Meta’s report.` : ''}
              {data.cached ? ' Cached — press Refresh for the latest.' : ''}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminRentSpacePage() {
  const [form, setForm]           = useState(EMPTY_CONFIG);
  const [configured, setConfigured] = useState(false);
  const [numbersInUse, setNumbersInUse] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  const [rentals, setRentals]     = useState([]);
  const [rentalsLoading, setRentalsLoading] = useState(true);
  const [deregisterTarget, setDeregisterTarget] = useState(null); // rental row or null
  const [busyRnId, setBusyRnId]   = useState(null);
  const [reviewBId, setReviewBId] = useState(null); // bId currently open in the flow-review modal, or null

  const loadConfig = useCallback(() => {
    setLoading(true);
    getRentSpaceConfig().then(res => {
      const c = res.data.config;
      setForm({
        token: c.token || '',
        wabaId: c.wabaId || '',
        appSecret: c.appSecret || '',
        monthlyPrice: c.monthlyPrice || '',
        flowEndpointUrl: c.flowEndpointUrl || '',
        metaAppId: c.metaAppId || '',
        // '' displays as a blank input meaning "unlimited" -- 0 would
        // render as a literal 0 in a number input, which reads as "no
        // one can rent anything" rather than "no cap".
        maxPerUser: c.maxPerUser ? String(c.maxPerUser) : '',
      });
      setConfigured(c.configured);
      setNumbersInUse(c.numbersInUse || 0);
    }).finally(() => setLoading(false));
  }, []);

  const loadRentals = useCallback(() => {
    setRentalsLoading(true);
    listRentSpaceRentals().then(res => {
      if (res.data?.success) setRentals(res.data.rentals || []);
    }).finally(() => setRentalsLoading(false));
  }, []);

  useEffect(() => { loadConfig(); loadRentals(); }, [loadConfig, loadRentals]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await saveRentSpaceConfig(form);
      toast.success('Rent Space settings saved');
      loadConfig();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handlePauseResume(rental) {
    setBusyRnId(rental.rnId);
    try {
      const action = rental.rnStatus === 'active' ? pauseRental : resumeRental;
      const res = await action(rental.rnId);
      if (res.data?.success) {
        toast.success(res.data.message);
        loadRentals();
      } else {
        toast.error(res.data?.message ?? 'Action failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setBusyRnId(null);
  }

  // Checks Meta's live state for this number -- our own database only
  // knows what we told it, so if someone deleted this manually in
  // WhatsApp Manager, our list would keep showing it forever without
  // this actually asking Meta whether it's still there.
  async function handleVerify(rental) {
    setBusyRnId(rental.rnId);
    try {
      const res = await verifyRentalStatus(rental.rnId);
      if (res.data?.success) {
        toast.success(res.data.message);
        if (res.data.removed) loadRentals();
      } else {
        toast.error(res.data?.message ?? 'Could not verify');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setBusyRnId(null);
  }

  if (loading) return <div className="bf-empty"><span className="spinner spinner-dark" /> Loading…</div>;

  return (
    <div className="bf-page" style={{ maxWidth: 900 }}>
      <PageHeader
        title="Rent Space"
        subtitle="Let customers register their own WhatsApp number under this platform's own WABA, billed monthly via PocketFi — no Meta developer account needed on their end."
      />

      <div className="bf-card" style={{ borderColor: configured ? 'var(--bf-success)' : 'var(--bf-warning)' }}>
        <div className="bf-card-body" style={{ fontSize: 13, color: configured ? 'var(--bf-success)' : 'var(--bf-warning)', fontWeight: 600 }}>
          {configured
            ? `✅ Rent Space is live — ${numbersInUse} of 20 numbers in use on this WABA (Meta's cap applies across your whole Business Manager, not per WABA — see note below).`
            : '⚠️ Not configured yet — set a token, WABA ID, App Secret, and monthly price below to enable this for customers.'}
        </div>
      </div>

      {configured && <RentUsagePanel />}

      <form onSubmit={handleSave} className="bf-card">
        <div className="bf-card-hdr">Platform WhatsApp Credentials</div>
        <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="bf-field">
            <label className="bf-field-label">WhatsApp Access Token</label>
            <input className="bf-input" value={form.token} onChange={e => setForm({ ...form, token: e.target.value })} placeholder="EAAxxxx…" />
            <div className="bf-field-hint">A System User token with whatsapp_business_management + business_management permissions. Leave unchanged (showing as ••••) to keep the current value.</div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">WABA ID</label>
            <input className="bf-input" value={form.wabaId} onChange={e => setForm({ ...form, wabaId: e.target.value })} placeholder="Platform's WhatsApp Business Account ID" />
          </div>
          <div className="bf-field">
            <label className="bf-field-label">
              Meta App ID
              <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6, color: 'var(--bf-text-muted, #9ca3af)' }}>(optional)</span>
            </label>
            <input className="bf-input" value={form.metaAppId} onChange={e => setForm({ ...form, metaAppId: e.target.value })} placeholder="1234567890123456" />
            <div className="bf-field-hint">
              Distinct from the WABA ID above — this is the Meta *App* ID (App Dashboard → App Settings → Basic → App ID).
              Only needed for the profile photo upload feature (Resumable Upload API). Same idea as the rest of this
              form: set it once here and it's used automatically for every rented number, so customers never need
              their own Meta App.
            </div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">App Secret</label>
            <input className="bf-input" value={form.appSecret} onChange={e => setForm({ ...form, appSecret: e.target.value })} placeholder="From Meta App Dashboard → App Settings → Basic" />
            <div className="bf-field-hint">
              The Meta App Secret for the app tied to the token above (App Dashboard → App Settings → Basic → App Secret, click "Show"). Meta signs every webhook with this — without it, incoming messages on rented numbers get silently dropped as "Signature mismatch." One value covers every rented number; renters never see or need this. Leave unchanged (showing as ••••) to keep the current value.
            </div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">Monthly Price (₦)</label>
            <input className="bf-input" type="number" min="0" step="1" value={form.monthlyPrice}
              onChange={e => setForm({ ...form, monthlyPrice: e.target.value })} placeholder="e.g. 5000" />
            <div className="bf-field-hint">Charged via PocketFi checkout — reuses the PocketFi credentials already configured on the PocketFi settings page.</div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">
              Max Spaces Per User
              <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6, color: 'var(--bf-text-muted, #9ca3af)' }}>(optional)</span>
            </label>
            <input className="bf-input" type="number" min="0" step="1" value={form.maxPerUser}
              onChange={e => setForm({ ...form, maxPerUser: e.target.value })} placeholder="Leave blank for unlimited" />
            <div className="bf-field-hint">
              How many rented numbers one customer may hold at once. Each rented number can have exactly one bot
              created from it for free — extra bots on the platform's shared WABA still cost the platform Meta
              phone-number slots, so this caps how many any single customer can occupy. Leave blank or 0 for no limit.
            </div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">
              General Flow Endpoint URL
              <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6, color: 'var(--bf-text-muted, #9ca3af)' }}>(optional)</span>
            </label>
            <input className="bf-input" value={form.flowEndpointUrl}
              onChange={e => setForm({ ...form, flowEndpointUrl: e.target.value })}
              placeholder="Leave blank to use this platform's own endpoint automatically" />
            <div className="bf-field-hint">
              Used as the Flow data-exchange / health-check endpoint for every rented number's Flows, so customers
              don't need their own server. Leave blank and each rented number automatically uses this platform's own
              built-in endpoint instead (api/bots/exchange) — only set this if you want rented-number Flow traffic
              routed somewhere else.
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>

      <SyncPlatformIdsPanel />

      <LeakedFlowsPanel />

      <div className="bf-card">
        <div className="bf-card-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Rented Numbers ({rentals.length})</span>
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }} onClick={loadRentals} disabled={rentalsLoading}>
            ↻ Refresh
          </button>
        </div>
        <div className="bf-card-body">
          {rentalsLoading ? (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>
          ) : rentals.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>No one has rented a number yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rentals.map(r => {
                const meta = STATUS_META[r.rnStatus] ?? STATUS_META.pending_payment;
                return (
                  <div key={r.rnId} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                    border: '1px solid #e5e7eb', borderRadius: 10, flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10, background: meta.bg, color: meta.color }}>
                      {meta.label}
                    </span>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                        {r.rnCountryCode && r.rnPhoneNumber ? `+${r.rnCountryCode}${r.rnPhoneNumber}` : '(not yet registered)'}
                        {r.rnDisplayName ? ` — ${r.rnDisplayName}` : ''}
                      </div>
                      <div style={{ fontSize: 11.5, color: '#9ca3af' }}>
                        {r.sEmail} {r.sFname ? `(${r.sFname} ${r.sLname || ''})` : ''} · ₦{Number(r.rnMonthlyAmount).toLocaleString()}/mo
                        {r.rnNextBillingAt ? ` · next billing ${new Date(r.rnNextBillingAt).toLocaleDateString()}` : ''}
                        {r.rnStatus === 'active' && r.rnNextBillingAt && new Date(r.rnNextBillingAt) <= new Date() && (
                          <span style={{ color: '#c0392b', fontWeight: 700 }}> · ⚠ overdue, bot paused</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(r.rnStatus === 'active' || r.rnStatus === 'paused') && (
                        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
                          onClick={() => handlePauseResume(r)} disabled={busyRnId === r.rnId}>
                          {r.rnStatus === 'active' ? 'Pause' : 'Resume'}
                        </button>
                      )}
                      {r.rnLinkedBotId && (
                        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
                          onClick={() => setReviewBId(r.rnLinkedBotId)}
                          title="See which local flows are on this customer's bot and remove any that leaked in from other renters">
                          🌊 Review Flows
                        </button>
                      )}
                      {r.rnPhoneNumberId && (
                        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
                          onClick={() => handleVerify(r)} disabled={busyRnId === r.rnId}
                          title="Check whether this number still exists on Meta's side">
                          {busyRnId === r.rnId ? '…' : '✓ Verify'}
                        </button>
                      )}
                      {r.rnPhoneNumberId && r.rnStatus !== 'deregistered' && (
                        <button className="btn btn-danger" style={{ fontSize: 12, padding: '5px 10px' }}
                          onClick={() => setDeregisterTarget(r)}>
                          Deregister
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {deregisterTarget && (
        <DeregisterConfirmModal
          rental={deregisterTarget}
          onClose={() => setDeregisterTarget(null)}
          onDeregistered={loadRentals}
        />
      )}

      {reviewBId && (
        <BotFlowsReviewModal bId={reviewBId} onClose={() => setReviewBId(null)} />
      )}
    </div>
  );
}